/**
 * RequireRole
 * CCH Care — Phase 2+ routing guard
 * Created: 2026-05-22
 *
 * Layout route guard that gates child routes by membership role.
 * Mirrors RequireOnShift's pattern (Outlet child + Navigate redirect)
 * but checks the user's role rather than shift state.
 *
 * Usage:
 *   <Route element={<RequireRole allow={['Care Supervisor', 'Care Admin']} />}>
 *     <Route path="residents" element={<ResidentsDirectoryPage />} />
 *   </Route>
 *
 * Redirects disallowed roles to /dashboard silently. This is
 * defense-in-depth — BottomNav hides the tab from disallowed roles
 * (per BottomNav.jsx's 2026-05-14 role gating), but a directly-pasted
 * URL would otherwise resolve and trigger a page-level 403 from the
 * backend. Catching it here gives a cleaner redirect than relying on
 * AccessDeniedHandler.
 *
 * `allow` is a static prop — no dynamic permission lookup. The role
 * list should mirror the backend's CARE_ROLE_PERMISSIONS for the
 * relevant permission. Keep in sync with BottomNav.jsx's VIEW_ALL_ROLES
 * for the Residents route specifically.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../features/auth/useAuth';

export default function RequireRole({ allow = [] }) {
  const { memberships } = useAuth();
  const role = memberships?.[0]?.role ?? '';

  if (!allow.includes(role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}